import React, { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { LayoutDashboard, Package, Users, ReceiptText, Store, LogOut, RefreshCcw, ShieldCheck, Settings, ClipboardList, Truck, BarChart3, Menu, X, DatabaseBackup } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/admin", end: true, label: "لوحة التحكم", icon: LayoutDashboard },
  { to: "/admin/products", label: "الموديلات والأسعار", icon: Package },
  { to: "/admin/orders", label: "الطلبات", icon: ClipboardList },
  { to: "/admin/invoices", label: "الفواتير", icon: ReceiptText },
  { to: "/admin/returns", label: "المرتجعات والاستبدال", icon: RefreshCcw },
  { to: "/admin/reports", label: "التقارير", icon: BarChart3 },
  { to: "/admin/delivery", label: "التوصيل والشحن", icon: Truck },
  { to: "/admin/users", label: "العاملات والمستخدمون", icon: Users },
  { to: "/admin/audit", label: "سجل التدقيق", icon: ShieldCheck },
  { to: "/admin/cleanup", label: "تنظيف التدريب (تجهيز)", icon: DatabaseBackup },
  { to: "/admin/settings", label: "السياسات والإعدادات", icon: Settings },
];

const DARK = "#141110";
const IVORY = "#F9F6F0";

function NavItems({ onNavigate }) {
  return (
    <>
      {NAV.map((n) => {
        const Icon = n.icon;
        return (
          <NavLink key={n.to} to={n.to} end={n.end} onClick={onNavigate} data-testid={`admin-nav-${n.to}`}
            className={({ isActive }) => `flex items-center gap-3 px-6 py-3 text-sm transition-colors ${isActive ? "bg-white/15 text-white border-e-2 border-[#B89C72]" : "text-white/70 hover:text-white hover:bg-white/5"}`}>
            <Icon className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} />
            <span>{n.label}</span>
          </NavLink>
        );
      })}
      <div className="mt-2 pt-2 border-t border-white/10" />
      <NavLink to="/" end onClick={onNavigate} data-testid="admin-view-store"
        className="flex items-center gap-3 px-6 py-3 text-sm text-white/70 hover:text-white hover:bg-white/5">
        <Store className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} /><span>عرض المتجر</span>
      </NavLink>
      <NavLink to="/pos" onClick={onNavigate} data-testid="admin-to-pos"
        className="flex items-center gap-3 px-6 py-3 text-sm text-white/70 hover:text-white hover:bg-white/5">
        <ClipboardList className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} /><span>نقطة البيع</span>
      </NavLink>
    </>
  );
}

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  useEffect(() => { setOpen(false); }, [loc.pathname]);

  const current = NAV.find((n) => (n.end ? loc.pathname === n.to : loc.pathname.startsWith(n.to))) || NAV[0];
  const doLogout = async () => { if (!window.confirm("تسجيل الخروج من لوحة الأونر؟")) return; await logout(); nav("/login", { replace: true }); };

  const Footer = () => (
    <div className="border-t border-white/10 py-2">
      <button onClick={doLogout} data-testid="admin-logout" className="flex items-center gap-3 px-6 py-3 text-sm text-white/70 hover:text-white w-full">
        <LogOut className="w-4 h-4" strokeWidth={1.5} /><span>تسجيل الخروج</span>
      </button>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F5F5F5] flex" dir="rtl" lang="ar">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-64 flex-col flex-shrink-0 sticky top-0 h-screen" style={{ background: DARK, color: IVORY }}>
        <div className="px-6 py-5 border-b border-white/10 flex justify-center">
          <img src="/logo-light.png" alt="RNF_Line" className="h-16 w-auto" />
        </div>
        <nav className="flex-1 py-4 overflow-y-auto">
          <NavItems />
        </nav>
        <Footer />
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="md:hidden fixed inset-0 z-50 flex" data-testid="admin-drawer">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <aside className="relative ms-auto w-72 max-w-[80%] flex flex-col h-full" style={{ background: DARK, color: IVORY }}>
            <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
              <img src="/logo-light.png" alt="RNF_Line" className="h-12 w-auto" />
              <button onClick={() => setOpen(false)} data-testid="admin-drawer-close" className="text-white/70 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <nav className="flex-1 py-4 overflow-y-auto"><NavItems onNavigate={() => setOpen(false)} /></nav>
            <Footer />
          </aside>
        </div>
      )}

      <main className="flex-1 min-w-0 overflow-x-hidden">
        {/* Mobile top bar */}
        <header className="md:hidden sticky top-0 z-30 flex items-center gap-3 px-4 py-3" style={{ background: DARK, color: IVORY }}>
          <button onClick={() => setOpen(true)} data-testid="admin-hamburger" aria-label="القائمة" className="text-white">
            <Menu className="w-6 h-6" />
          </button>
          <span className="text-sm font-medium">{current.label}</span>
          <button onClick={doLogout} data-testid="admin-logout-mobile" className="ms-auto text-white/70 hover:text-white"><LogOut className="w-5 h-5" /></button>
        </header>
        <Outlet />
      </main>
    </div>
  );
}
